import React, {useEffect} from 'react'
import PropTypes from 'prop-types'
import {Box, Heading, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import CloudinaryImage from '../../../cloudinary-media/image'
import {RESIZE_FULL_HERO, RESIZE_HALF_MOBILE_ONE_FOURTH_DESKTOP} from '../../../../constants'
import Video from '../hero/partials/video'
import PDComponent from '../../pd-component'
import usePdTagManager from '../../../../hooks/use-pd-tag-manager'

const locationToFlex = {
    'top-left': {
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        textAlign: 'start',
        float: 'inline-start'
    },
    'top-center': {
        justifyContent: 'center',
        alignItems: 'flex-start',
        textAlign: 'center',
        float: 'unset'
    },
    'top-right': {
        justifyContent: 'flex-end',
        alignItems: 'flex-start',
        textAlign: 'end',
        float: 'inline-end'
    },
    'middle-left': {
        justifyContent: 'flex-start',
        alignItems: 'center',
        textAlign: 'start',
        float: 'inline-start'
    },
    'middle-center': {
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        float: 'unset'
    },
    'middle-right': {
        justifyContent: 'flex-end',
        alignItems: 'center',
        textAlign: 'end',
        float: 'inline-end'
    },
    'bottom-left': {
        justifyContent: 'flex-start',
        alignItems: 'flex-end',
        textAlign: 'start',
        float: 'inline-start'
    },
    'bottom-center': {
        justifyContent: 'center',
        alignItems: 'flex-end',
        textAlign: 'center',
        float: 'unset'
    },
    'bottom-right': {
        justifyContent: 'flex-end',
        alignItems: 'flex-end',
        textAlign: 'end',
        float: 'inline-end'
    }
}

export const HeroMultipleCTAa = (props) => {
    const {
        eagerLoadImage,
        headlineText,
        headlineColor,
        headlineFontFamily,
        headlineTag,

        descriptionText,
        descriptionColor,
        descriptionFontFamily,
        descriptionTag,

        childLocation,
        blackOpacityOverlay,

        desktopImage1,
        desktopImage2,
        desktopImage3,
        desktopImage4,
        mobileImage1,
        mobileImage2,
        mobileImage3,
        mobileImage4,
        imageAlt1,
        imageAlt2,
        imageAlt3,
        imageAlt4,

        showVideo,
        desktopVideo,
        mobileVideo,
        desktopVideoThumbnail,
        mobileVideoThumbnail,
        videoAlt,
        videoEnableSound,
        videoDisableAutoPlay,

        typeId,
        name
    } = props
    const pdTagManager = usePdTagManager()
    const locationStyle = locationToFlex[childLocation] || locationToFlex['middle-center']
    const dontShowSlider = props?.regions?.[0]?.components?.[0]?.data?.dontShowSlider
    const images = [
        {desktop: desktopImage1, mobile: mobileImage1, alt: imageAlt1},
        {desktop: desktopImage2, mobile: mobileImage2, alt: imageAlt2},
        {desktop: desktopImage3, mobile: mobileImage3, alt: imageAlt3},
        {desktop: desktopImage4, mobile: mobileImage4, alt: imageAlt4}
    ].filter((img) => img.desktop || img.mobile)

    const hasSingleImage = images.length === 1
    const singleImg = hasSingleImage ? images[0] : null

    const sizeDesktop = {width: 1440, height: 512}
    const sizeMobile = {width: 414, height: 512}
    useEffect(() => {
        const totalLinks = props?.regions?.[0]?.components?.[0]?.regions?.[0]?.components?.length
        if (totalLinks) {
            pdTagManager.viewComponent(typeId, name, totalLinks)
        }
    }, [typeId])

    return (
        <Box display="flex" width="100%" height="100%" position="relative">
            {showVideo && (
                <Box display="contents">
                    <Video
                        desktopVideo={desktopVideo}
                        mobileVideo={mobileVideo || desktopVideo}
                        desktopVideoThumbnail={desktopVideoThumbnail}
                        mobileVideoThumbnail={mobileVideoThumbnail}
                        videoDisableAutoPlay={videoDisableAutoPlay}
                        videoEnableSound={videoEnableSound}
                        videoAlt={videoAlt}
                        variant="hero"
                        mediaSize="hero"
                        desktopMediaPosition="full"
                        mobileMediaPosition="full"
                    />
                </Box>
            )}
            {!showVideo && (
                <Box
                    sx={{
                        '.image-parent::before': {
                            content: "''",
                            position: 'absolute',
                            inset: 0,
                            background: `linear-gradient(to bottom, rgba(0, 0, 0, ${blackOpacityOverlay}), rgba(0, 0, 0, 0))`,
                            pointerEvents: 'none',
                            zIndex: 1
                        }
                    }}
                >
                    {hasSingleImage ? (
                        <>
                            {/* Desktop image */}
                            <Box
                                display={{base: 'none', md: 'block'}}
                                height="100%"
                                width="100%"
                                className="image-parent"
                            >
                                <CloudinaryImage
                                    src={singleImg.desktop}
                                    aspectRatio={`${sizeDesktop.width}:${sizeDesktop.height}`}
                                    imgSizes={{base: '100vw'}}
                                    lazyload={!eagerLoadImage}
                                    sizes={RESIZE_FULL_HERO}
                                    alt={singleImg.alt}
                                    styles={{
                                        aspectRatio: `${sizeDesktop.width} / ${sizeDesktop.height}`,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        htmlWidth: sizeDesktop.width,
                                        htmlHeight: sizeDesktop.height
                                    }}
                                />
                            </Box>

                            {/* Mobile image */}
                            <Box
                                display={{base: 'block', md: 'none'}}
                                height="100%"
                                width="100%"
                                className="image-parent"
                            >
                                <CloudinaryImage
                                    src={singleImg.mobile}
                                    aspectRatio={`${sizeMobile.width}:${sizeMobile.height}`}
                                    imgSizes={{base: '100vw'}}
                                    lazyload={!eagerLoadImage}
                                    sizes={RESIZE_FULL_HERO}
                                    alt={singleImg.alt}
                                    styles={{
                                        aspectRatio: `${sizeMobile.width} / ${sizeMobile.height}`,
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover',
                                        htmlWidth: sizeMobile.width,
                                        htmlHeight: sizeMobile.height
                                    }}
                                />
                            </Box>
                        </>
                    ) : (
                        <Box
                            display="flex"
                            flexWrap="wrap"
                            width="100%"
                            height="100%"
                            className="image-parent"
                        >
                            {images.map((img, idx) => (
                                <Box
                                    key={idx}
                                    width={{base: '50%', md: '50%', lg: '25%'}} // 2 per row (base/md), 4 per row (lg+)
                                    position="relative"
                                    height="auto"
                                    flex="none"
                                >
                                    <CloudinaryImage
                                        src={img.desktop}
                                        aspectRatio="480:534"
                                        lazyload={!eagerLoadImage}
                                        styles={{
                                            display: {base: 'none', md: 'block'},
                                            aspectRatio: '480 / 534',
                                            width: '100%',
                                            height: 'auto',
                                            htmlWidth: 480,
                                            htmlHeight: 534
                                        }}
                                        imgSizes={{lg: '25vw', md: '50vw', base: '50vw'}}
                                        sizes={RESIZE_HALF_MOBILE_ONE_FOURTH_DESKTOP}
                                        alt={img.alt}
                                    />
                                    <CloudinaryImage
                                        src={img.mobile}
                                        aspectRatio="237:264"
                                        lazyload={!eagerLoadImage}
                                        styles={{
                                            display: {base: 'block', md: 'none'},
                                            aspectRatio: '237 / 264',
                                            width: '100%',
                                            height: 'auto',
                                            htmlWidth: 237,
                                            htmlHeight: 264
                                        }}
                                        imgSizes={{lg: '25vw', md: '50vw', base: '50vw'}}
                                        sizes={RESIZE_HALF_MOBILE_ONE_FOURTH_DESKTOP}
                                        alt={img.alt}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            )}

            {/* Overlay text + buttons */}
            <Box
                position="absolute"
                top="0"
                left="0"
                width="100%"
                height="100%"
                display="flex"
                flexDirection="column"
                justifyContent={locationStyle.alignItems}
                alignItems={locationStyle.justifyContent}
                textAlign="center"
                zIndex={1}
            >
                <Box
                    p={{
                        base:
                            locationStyle?.alignItems === 'center'
                                ? '0px 20px 0px 20px'
                                : '20px 20px 0px 20px',
                        lg:
                            locationStyle?.alignItems === 'center'
                                ? '0px 30px 0px 30px'
                                : '30px 30px 0px 30px',
                        xl:
                            locationStyle?.alignItems === 'center'
                                ? '0px 120px 0px 120px'
                                : '60px 120px 0px 120px'
                    }}
                >
                    <Box textAlign={locationStyle?.textAlign}>
                        <Heading
                            fontWeight="500"
                            color={headlineColor}
                            fontFamily={headlineFontFamily}
                            as={headlineTag}
                            marginBottom="10px"
                            letterSpacing="0.24px"
                            fontSize={{base: '48px', lg: '64px'}}
                        >
                            {headlineText}
                        </Heading>
                    </Box>
                    <Box mb="20px">
                        <Text
                            fontWeight="300"
                            letterSpacing="0.085px"
                            textAlign={locationStyle?.textAlign}
                            color={descriptionColor}
                            fontFamily={descriptionFontFamily}
                            as={descriptionTag}
                            fontSize={{base: '16px', md: '17px'}}
                        >
                            {descriptionText}
                        </Text>
                    </Box>
                </Box>
                {/* Render all subcomponents inside each region */}
                <Box
                    sx={{
                        '.swiper-wrapper': {
                            paddingLeft: {
                                base: '20px !important',
                                lg: '30px !important',
                                xl:
                                    locationStyle?.justifyContent === 'center' && !dontShowSlider
                                        ? '0px'
                                        : '120px !important'
                            },
                            width: '100vw',
                            justifyContent: locationStyle?.justifyContent
                        },
                        '.swiper-slide': {
                            '.chakra-text': {
                                width: 'fit-content'
                            }
                        },
                        '.text-links-box .swiper-slide, .text-links-box .chakra-text': {
                            width: 'auto',
                            flexGrow: 'unset'
                        },
                        '.text-links-box': {
                            justifyContent: locationStyle?.justifyContent,
                            marginBottom: {
                                base:
                                    locationStyle?.alignItems === 'flex-end' && !dontShowSlider
                                        ? '20px'
                                        : '0px',
                                lg:
                                    locationStyle?.alignItems === 'flex-end' && !dontShowSlider
                                        ? '30px'
                                        : '0px',
                                xl:
                                    locationStyle?.alignItems === 'flex-end' && !dontShowSlider
                                        ? '60px'
                                        : '0px'
                            },
                            display:
                                locationStyle?.justifyContent === 'center'
                                    ? 'flex'
                                    : dontShowSlider
                                    ? 'contents'
                                    : 'block'
                        },
                        '.button-link': {
                            height: '30px',
                            width: 'min-content',
                            borderRadius: '90px'
                        },
                        '.links-flex': {
                            rowGap: {base: '10px', lg: '20px'},
                            columnGap: '20px',
                            float: locationStyle?.float,
                            justifyContent: locationStyle?.justifyContent,
                            padding: {
                                base:
                                    locationStyle?.justifyContent === 'center' &&
                                    !locationStyle?.alignItems === 'flex-end'
                                        ? '0px'
                                        : locationStyle?.justifyContent === 'flex-end'
                                        ? locationStyle?.alignItems === 'flex-end'
                                            ? '0px 20px 20px 20px'
                                            : '0px 20px 0px 20px'
                                        : locationStyle?.alignItems === 'flex-end'
                                        ? locationStyle?.justifyContent === 'center'
                                            ? '0px 0px 20px 0px'
                                            : '0px 0px 20px 20px'
                                        : locationStyle?.justifyContent === 'center'
                                        ? '0px 0px 0px 0px'
                                        : '0px 0px 0px 20px',
                                lg:
                                    locationStyle?.justifyContent === 'center' &&
                                    !locationStyle?.alignItems === 'flex-end'
                                        ? '0px'
                                        : locationStyle?.justifyContent === 'flex-end'
                                        ? locationStyle?.alignItems === 'flex-end'
                                            ? '0px 30px 30px 30px'
                                            : '0px 30px 0px 30px'
                                        : locationStyle?.alignItems === 'flex-end'
                                        ? locationStyle?.justifyContent === 'center'
                                            ? '0px 0px 30px 0px'
                                            : '0px 0px 30px 30px'
                                        : locationStyle?.justifyContent === 'center'
                                        ? '0px 0px 0px 0px'
                                        : '0px 0px 0px 30px',
                                xl:
                                    locationStyle?.justifyContent === 'center' &&
                                    !locationStyle?.alignItems === 'flex-end'
                                        ? '0px'
                                        : locationStyle?.justifyContent === 'flex-end'
                                        ? locationStyle?.alignItems === 'flex-end'
                                            ? '0px 120px 60px 120px'
                                            : '0px 120px 0px 120px'
                                        : locationStyle?.alignItems === 'flex-end'
                                        ? locationStyle?.justifyContent === 'center'
                                            ? '0px 0px 60px 0px'
                                            : '0px 0px 60px 120px'
                                        : locationStyle?.justifyContent === 'center'
                                        ? '0px 0px 0px 0px'
                                        : '0px 0px 0px 120px'
                            }
                        }
                    }}
                >
                    {props?.regions?.map((region) =>
                        region?.components?.map((component, index) => (
                            <PDComponent key={`${region.id}-${index}`} component={component} />
                        ))
                    )}
                </Box>
            </Box>
        </Box>
    )
}

HeroMultipleCTAa.propTypes = {
    eagerLoadImage: PropTypes.bool,
    headlineText: PropTypes.string,
    headlineColor: PropTypes.string,
    headlineFontFamily: PropTypes.string,
    headlineTag: PropTypes.string,

    descriptionText: PropTypes.string,
    descriptionColor: PropTypes.string,
    descriptionFontFamily: PropTypes.string,
    descriptionTag: PropTypes.string,

    childLocation: PropTypes.string,
    blackOpacityOverlay: PropTypes.string,

    desktopImage1: PropTypes.string,
    desktopImage2: PropTypes.string,
    desktopImage3: PropTypes.string,
    desktopImage4: PropTypes.string,
    mobileImage1: PropTypes.string,
    mobileImage2: PropTypes.string,
    mobileImage3: PropTypes.string,
    mobileImage4: PropTypes.string,

    imageAlt1: PropTypes.string,
    imageAlt2: PropTypes.string,
    imageAlt3: PropTypes.string,
    imageAlt4: PropTypes.string,
    showVideo: PropTypes.bool,

    desktopVideo: PropTypes.string,
    mobileVideo: PropTypes.string,
    desktopVideoThumbnail: PropTypes.string,
    mobileVideoThumbnail: PropTypes.string,
    videoAlt: PropTypes.string,
    videoEnableSound: PropTypes.bool,
    videoDisableAutoPlay: PropTypes.bool,

    regions: PropTypes.array,
    typeId: PropTypes.string,
    name: PropTypes.any
}

export default HeroMultipleCTAa
